import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Inbox, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '../../../components/design-system/admin/EmptyState';
import { PillTabs } from '../../../components/design-system/admin/PillTabs';
import { RelativeTime } from '../../../components/design-system/admin/RelativeTime';
import { SearchField } from '../../../components/design-system/admin/SearchField';
import { SectionCard } from '../../../components/design-system/admin/SectionCard';
import { FilterChip } from '../../../components/design-system/common/FilterChip';
import { Thumb } from '../../../components/design-system/analytics/Thumb';
import { useShopperRequestsApi } from '../../../common/hooks/use-shopper-requests-api.hook';
import {
    STATUS_FILTERS,
    StatusFilter,
    areaFigure,
    formatDay,
    formatMoney,
    parseKindFilter,
    parseStatusFilter,
    productLine,
    sourceKey,
    statusFilterCount,
    statusesFor,
    upperKey,
    whoOf,
} from './request-format';
import { KindTile, RequestStatusBadge, TestBadge } from './request-presentation';
import { REQUEST_KINDS, RequestCounts, RequestDetail, RequestKind, RequestView } from './types';

const PAGE_SIZE = 30;

/**
 * The inbox: every request a shopper sent, newest first, filtered by status
 * (Open by default — what still needs the vendor) and by kind, searchable by
 * name, email, phone or product. A row opens the request's drawer.
 */
export function RequestsTab({
    onOpen,
    onOpenSettings,
    patched,
}: {
    onOpen: (request: RequestView) => void;
    onOpenSettings: () => void;
    /** The last request the drawer changed: its row is updated in place. */
    patched: RequestDetail | null;
}) {
    const { t, i18n } = useTranslation();
    const api = useShopperRequestsApi();
    const [params, setParams] = useSearchParams();
    const status = parseStatusFilter(params.get('status'));
    const kind = parseKindFilter(params.get('kind'));
    const [search, setSearch] = useState('');
    const [q, setQ] = useState('');

    const [items, setItems] = useState<RequestView[]>([]);
    const [counts, setCounts] = useState<RequestCounts | null>(null);
    const [nextBefore, setNextBefore] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(false);
    const latest = useRef(0);

    useEffect(() => {
        const id = setTimeout(() => setQ(search.trim()), 300);
        return () => clearTimeout(id);
    }, [search]);

    const query = useMemo(() => ({ status: statusesFor(status), kind: kind ? [kind] : undefined, q }), [status, kind, q]);

    const load = useCallback(async () => {
        const id = ++latest.current;
        setLoading(true);
        setError(false);
        try {
            const r = await api.list({ ...query, limit: PAGE_SIZE });
            if (id !== latest.current) return;
            setItems(r?.items ?? []);
            setCounts(r?.counts ?? null);
            setNextBefore(r?.nextBefore ?? null);
        } catch {
            if (id === latest.current) setError(true);
        } finally {
            if (id === latest.current) setLoading(false);
        }
        // The api object is rebuilt with the auth context; the query is what matters.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    useEffect(() => {
        void load();
    }, [load]);

    // A request changed in the drawer: show its new status here at once, and
    // refresh the counts. The row stays even if it no longer matches the
    // filter — vanishing under the cursor reads as a bug.
    useEffect(() => {
        if (!patched) return;
        setItems((list) => list.map((r) => (r.uuid === patched.uuid ? { ...r, ...patched } : r)));
        api.list({ limit: 1 })
            .then((r) => r?.counts && setCounts(r.counts))
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patched]);

    const loadMore = async () => {
        if (!nextBefore) return;
        const id = latest.current;
        setLoadingMore(true);
        try {
            const r = await api.list({ ...query, limit: PAGE_SIZE, before: nextBefore });
            if (id !== latest.current) return;
            setItems((list) => [...list, ...(r?.items ?? []).filter((x) => !list.some((y) => y.uuid === x.uuid))]);
            setNextBefore(r?.nextBefore ?? null);
        } catch {
            setError(true);
        } finally {
            setLoadingMore(false);
        }
    };

    const setFilter = (key: 'status' | 'kind', value: string | null) =>
        setParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (value) next.set(key, value);
                else next.delete(key);
                return next;
            },
            { replace: true },
        );

    const nothingYet = !loading && !error && counts !== null && statusFilterCount('all', counts) === 0;
    const lang = i18n.language;

    if (nothingYet) {
        return (
            <Card className="py-2" data-testid="sr-empty">
                <EmptyState
                    icon={<Inbox />}
                    title={t('SHOPPER_REQUESTS.EMPTY.TITLE')}
                    description={t('SHOPPER_REQUESTS.EMPTY.BODY')}
                    action={
                        <Button variant="outline" size="sm" onClick={onOpenSettings}>
                            {t('SHOPPER_REQUESTS.EMPTY.ACTION')}
                        </Button>
                    }
                />
            </Card>
        );
    }

    return (
        <SectionCard flush testId="sr-requests">
            <div className="flex flex-col gap-3 px-4 pb-4 sm:px-6">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <PillTabs<StatusFilter>
                        value={status}
                        onChange={(v) => setFilter('status', v === 'open' ? null : v)}
                        ariaLabel={t('SHOPPER_REQUESTS.FILTER_STATUS')}
                        options={STATUS_FILTERS.map((f) => ({
                            value: f,
                            label: t(`SHOPPER_REQUESTS.STATUS_FILTER.${upperKey(f)}`),
                            count: statusFilterCount(f, counts),
                        }))}
                    />
                    <SearchField value={search} onChange={setSearch} placeholder={t('SHOPPER_REQUESTS.SEARCH')} />
                </div>
                <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('SHOPPER_REQUESTS.FILTER_KIND')}>
                    {REQUEST_KINDS.map((k) => (
                        <FilterChip
                            key={k}
                            selected={kind === k}
                            onClick={() => setFilter('kind', kind === k ? null : k)}
                            label={
                                <span className="inline-flex items-center gap-1.5">
                                    {t(`SHOPPER_REQUESTS.KIND.${upperKey(k)}`)}
                                    {!!counts?.openByKind?.[k] && <span className="tabular-nums text-muted-foreground">{counts.openByKind[k]}</span>}
                                </span>
                            }
                        />
                    ))}
                </div>
            </div>

            <div className="border-t" data-testid="sr-list">
                {error && !items.length ? (
                    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-6 sm:px-6">
                        <p className="text-sm text-muted-foreground">{t('SHOPPER_REQUESTS.LOAD_FAILED')}</p>
                        <Button variant="outline" size="sm" onClick={() => void load()}>
                            {t('SHOPPER_REQUESTS.RETRY')}
                        </Button>
                    </div>
                ) : loading && !items.length ? (
                    <ul className="divide-y">
                        {[0, 1, 2, 3].map((i) => (
                            <li key={i} className="flex items-center gap-3 px-4 py-4 sm:px-6">
                                <Skeleton className="size-9 rounded-lg" />
                                <div className="flex-1 space-y-2">
                                    <Skeleton className="h-4 w-48" />
                                    <Skeleton className="h-3 w-72 max-w-full" />
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : items.length === 0 ? (
                    <p className="px-4 py-10 text-center text-sm text-muted-foreground sm:px-6">{t('SHOPPER_REQUESTS.NO_MATCH')}</p>
                ) : (
                    <ul className={cn('divide-y transition-opacity', loading && 'opacity-60')}>
                        {items.map((r) => (
                            <RequestRow key={r.uuid} request={r} lang={lang} onOpen={() => onOpen(r)} />
                        ))}
                    </ul>
                )}
            </div>

            {nextBefore && items.length > 0 && (
                <div className="flex justify-center border-t px-4 py-3">
                    <Button variant="outline" size="sm" onClick={() => void loadMore()} disabled={loadingMore} data-testid="sr-load-more">
                        {loadingMore && <Loader2 className="animate-spin" />}
                        {t('SHOPPER_REQUESTS.LOAD_MORE')}
                    </Button>
                </div>
            )}
        </SectionCard>
    );
}

function RequestRow({ request, lang, onOpen }: { request: RequestView; lang: string; onOpen: () => void }) {
    const { t } = useTranslation();
    const who = whoOf(request) || t('SHOPPER_REQUESTS.ANONYMOUS');
    const line = productLine(request.items);
    const area = areaFigure(request.areaM2, request.areaRange, lang);
    const total = formatMoney(request.total, request.currency, lang);
    const day = request.kind === 'visit' ? formatDay(request.preferredDate, lang) : null;
    const when = day && request.preferredSlot ? `${day} · ${t(`SHOPPER_REQUESTS.SLOT.${upperKey(request.preferredSlot)}`)}` : day;

    return (
        <li>
            <button
                type="button"
                onClick={onOpen}
                className="flex w-full items-start gap-3 px-4 py-3.5 text-start transition-colors hover:bg-muted/40 sm:px-6"
                data-testid="sr-row"
                data-kind={request.kind}
                data-status={request.status}
            >
                <KindTile kind={request.kind as RequestKind} />
                <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-medium">{who}</span>
                        {request.internal && <TestBadge />}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                        {t(`SHOPPER_REQUESTS.KIND.${upperKey(request.kind)}`)} · {t(`SHOPPER_REQUESTS.SOURCE.${sourceKey(request.source)}`)}
                    </p>
                    {line && (
                        <div className="mt-2 flex min-w-0 items-center gap-2">
                            <Thumb src={line.thumbnailUrl} alt="" size="xs" />
                            <span className="truncate text-xs">{line.title}</span>
                            {line.more > 0 && <span className="shrink-0 text-xs tabular-nums text-muted-foreground">+{line.more}</span>}
                        </div>
                    )}
                    {(area || total || request.store?.name || when) && (
                        <p className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs tabular-nums text-muted-foreground">
                            {area && <span>{t(area.kind === 'range' ? 'SHOPPER_REQUESTS.AREA_RANGE' : 'SHOPPER_REQUESTS.AREA_ABOUT', { area: area.text })}</span>}
                            {total && <span className="text-foreground">{total}</span>}
                            {request.store?.name && <span className="truncate">{request.store.name}</span>}
                            {when && <span>{when}</span>}
                        </p>
                    )}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                    <RequestStatusBadge status={request.status} />
                    <RelativeTime value={request.createdAt} className="text-xs text-muted-foreground" />
                </div>
            </button>
        </li>
    );
}
